// Sentry error monitoring + performance tracing + masked session replay for
// the React frontend. Safe no-op when REACT_APP_SENTRY_DSN is unset.
//
// PII protection: identity fields are dropped and any key that looks
// sensitive (email, GSTIN, payment ref, token, etc.) is redacted before the
// event leaves the browser. Replay masks all text and blocks all media.
import * as Sentry from "@sentry/react";

const SENSITIVE = /(email|gstin|pan|phone|mobile|otp|payment|payment_ref|card|authorization|password|secret|token|cookie|jwt|api[_-]?key|dsn)/i;

const redact = (value) => {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, SENSITIVE.test(k) ? "[REDACTED]" : redact(v)])
    );
  }
  return value;
};

const dsn = process.env.REACT_APP_SENTRY_DSN;

// Auto-detect environment from the live domain so we never depend on a
// per-environment build var being set correctly: indusfact.com => production,
// the preview host => preview. Falls back to the env var, then 'development'.
const _host = typeof window !== "undefined" ? window.location.hostname : "";
const environment = _host.endsWith("indusfact.com")
  ? "production"
  : _host.includes("preview.emergentagent.com")
  ? "preview"
  : process.env.REACT_APP_SENTRY_ENVIRONMENT || "development";

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      return redact(event);
    },
    beforeSendTransaction(event) {
      return redact(event);
    },
  });
}

export default Sentry;
